import React, { useState } from 'react';
import { Download, FileText, CheckCircle, Calendar, Linkedin, Link2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const ShareButtons = ({ slug, title }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/resources#whitepapers`;
  const text = `${title} — a whitepaper from Momentedge`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const x = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link', shareUrl);
    }
  };

  const cls = 'inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-700 bg-slate-800/60 text-slate-300 hover:text-white hover:border-slate-500 text-sm font-medium transition-colors';

  return (
    <div className="mt-8 pt-6 border-t border-slate-700/60 flex flex-wrap items-center gap-3" data-testid={`whitepaper-share-${slug}`}>
      <span className="text-sm text-slate-500 mr-1">Share</span>
      <a href={linkedin} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`share-linkedin-${slug}`} aria-label="Share on LinkedIn">
        <Linkedin size={16} /> LinkedIn
      </a>
      <a href={x} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`share-x-${slug}`} aria-label="Share on X">
        <XIcon /> X
      </a>
      <button type="button" onClick={copy} className={cls} data-testid={`share-copy-${slug}`} aria-label="Copy link">
        {copied ? <Check size={16} className="text-green-400" /> : <Link2 size={16} />} {copied ? 'Copied' : 'Copy link'}
      </button>
    </div>
  );
};

export const WhitepaperCard = ({ slug, title, date, summary, points, pdfUrl, badge }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, whitepaper: title }),
      });
      if (!response.ok) throw new Error('Failed to submit email');
      setSubmitted(true);
      setIsLoading(false);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
      alert('There was an error. Please try again.');
    }
  };

  return (
    <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden" data-testid={`whitepaper-card-${slug}`}>
      <div className="grid md:grid-cols-2 gap-8 p-8 md:p-10">
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium">
              <FileText size={16} />
              <span>{badge || 'Whitepaper'}</span>
            </div>
            {date && (
              <div className="inline-flex items-center space-x-2 text-slate-400 text-sm">
                <Calendar size={16} />
                <span>{date}</span>
              </div>
            )}
          </div>

          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">{title}</h3>
          <p className="text-lg text-slate-300 mb-6 leading-relaxed">{summary}</p>

          <div className="space-y-3 text-slate-400">
            {points.map((point) => (
              <div key={point} className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span>{point}</span>
              </div>
            ))}
          </div>

          <ShareButtons slug={slug} title={title} />
        </div>

        <div className="bg-slate-900/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700/30">
          {!submitted ? (
            <>
              <h4 className="text-xl font-bold text-white mb-4">Download the Whitepaper</h4>
              <p className="text-slate-400 mb-6">Enter your email to receive instant access to the full whitepaper.</p>

              <form onSubmit={handleDownload} className="space-y-4">
                <div>
                  <label htmlFor={`email-${slug}`} className="block text-sm font-medium text-slate-300 mb-2">
                    Email Address
                  </label>
                  <Input
                    id={`email-${slug}`}
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid={`whitepaper-email-input-${slug}`}
                    className="w-full bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  data-testid={`whitepaper-submit-button-${slug}`}
                  className="w-full bg-white text-slate-900 hover:bg-slate-100 font-semibold py-3"
                >
                  {isLoading ? 'Processing...' : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  By downloading, you agree to receive occasional updates from Momentedge.
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-8" data-testid={`whitepaper-success-${slug}`}>
              <div className="w-16 h-16 bg-green-500/20 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h4 className="text-xl font-bold text-white mb-2">Download Started!</h4>
              <p className="text-slate-300 mb-4">Your whitepaper should be downloading now.</p>
              <p className="text-sm text-slate-400">
                Didn't start? <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">Click here</a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
